/*@jsxRuntime automatic @jsxImportSource react*/
import {jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
import Head from 'next/head';
import styled from 'styled-components';
import {TextButton} from '@calm-web/design-system';
import MDXLink from '@/components/terms/MarkdownUtils/MDXLink';
import {Columns, UpdatedDate, Wrapper} from '@/components/terms/styles';
export const meta = {
  title: 'Calm Consumer Health Data Privacy Policy'
};
function _createMdxContent(props) {
  const _components = Object.assign({
    h1: "h1",
    p: "p",
    h2: "h2",
    ul: "ul",
    li: "li",
    strong: "strong"
  }, _provideComponents(), props.components);
  return _jsxs(Wrapper, {
    children: [_jsx(_components.h1, {
      children: "Calm Consumer Health Data Privacy Policy"
    }), _jsx(UpdatedDate, {
      children: "Last Updated August 15, 2025"
    }), _jsxs(_components.p, {
      children: ["This Consumer Health Privacy Policy explains how Calm.com, Inc. and our subsidiaries and affiliates (“Calm”, “we”, “our”, or “us”) collect, use, and disclose certain health information about you when you access or use our Calm Sleep website(s) and mobile application (collectively, the “Services”), and when you contact our customer service team or otherwise interact with us. This Consumer Health Privacy Policy is a supplement to the ", _jsx(MDXLink, {
        text: "Calm Privacy Policy",
        href: "/privacy-policy",
        label: "Calm Privacy Policy"
      }), " and applies to personal information and other data that may fall within the definition of “consumer health data” under applicable United States state laws, including Nevada’s Consumer Health Data Privacy Act and Washington’s My Health My Data Act (“Consumer Health Data”). We encourage you to review the Calm Privacy Policy to learn more about our privacy practices and our commitments to you. Capitalized terms used below have the same definitions as in the ", _jsx(MDXLink, {
        text: "Calm Privacy Policy",
        href: "/privacy-policy",
        label: "Calm Privacy Policy"
      }), " or in the applicable laws, unless otherwise noted."]
    }), _jsx(_components.p, {
      children: "We may change this Consumer Health Privacy Policy from time to time. If we make changes, we will revise the date at the top of the policy and, in some cases, we may provide you with additional notice."
    }), _jsx(_components.p, {
      children: "This Consumer Health Privacy Policy does not apply to third-party websites or producats. Please separately review their privacy policies to understand how your personal information is used and stored by those third parties."
    }), _jsxs(_components.h2, {
      children: [_jsx("a", {
        name: "collection-of-consumer-health-data"
      }), "Collection of Consumer Health Data"]
    }), _jsx(_components.p, {
      children: "We may collect certain Consumer Health Data about you depending on how you engage with our Services. This health information may fall under the following specific categories of Consumer Health Data:"
    }), _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Bodily functions, vital signs, symptoms, or measurements. With your permission, we may receive data from your mobile device’s health app (like Apple HealthKit or Google Health Connect) or wearable, such as hours of sleep, heart rate, wrist temperature, and blood oxygen."
      }), "\n"]
    }), _jsx(_components.h2, {
      children: "Sources of Consumer Health Data"
    }), _jsx(_components.p, {
      children: "We may collect Consumer Health Data in the following ways:"
    }), _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "from third-party apps (like Apple HealthKit) or other third-party products (like a connected fitness device) with which you have chosen to integrate or link your Calm Sleep account."
      }), "\n"]
    }), _jsxs(_components.h2, {
      children: [_jsx("a", {
        name: "use-of-consumer-health-data"
      }), "Use of Consumer Health Data"]
    }), _jsx(_components.p, {
      children: "We may use your Consumer Health Data in the following ways:"
    }), _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "to provide our Services to you, including by personalizing your Calm Sleep experience and sending you reports on your sleep and recommended actions;"
      }), "\n", _jsx(_components.li, {
        children: "to provide you support when responding to your questions, concerns, complaints, or other requests;"
      }), "\n", _jsx(_components.li, {
        children: "to create anonymous or aggregated data sets;"
      }), "\n", _jsx(_components.li, {
        children: "to protect ourselves, you, and others such as by taking actions to prevent, detect, or respond to fraud, security incidents, harassment, or other malicious or illegal activity;"
      }), "\n", _jsx(_components.li, {
        children: "to comply with our compliance, third party contractual agreements, and other legal obligations;"
      }), "\n", _jsx(_components.li, {
        children: "to establish or exercise our legal rights or defense against legal claims; and"
      }), "\n", _jsx(_components.li, {
        children: "otherwise with your consent where requested and received."
      }), "\n"]
    }), _jsxs(_components.h2, {
      children: [_jsx("a", {
        name: "disclosures-of-consumer-health-data"
      }), "Disclosures of Consumer Health Data"]
    }), _jsx(_components.p, {
      children: "We may disclose your Consumer Health Data to achieve the purposes and uses listed above. The categories of recipients to whom we disclose your Consumer Health Data with are:"
    }), _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Service providers:"
        }), " We disclose Consumer Health Data to service providers and vendors (also known as “processors” under the Consumer Health Data Laws) that need access to such data to perform services on our behalf, such as to provide cloud storage, manage user accounts, and power our Services."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Business transfer:"
        }), " We may disclose Consumer Health Data in connection with any merger, sale of company assets, financing or acquisition of all or a portion of our business by another company."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Other:"
        }), " We may disclose Consumer Health Data for legal reasons or as necessary to protect Calm or others."]
      }), "\n"]
    }), _jsxs(_components.h2, {
      children: [_jsx("a", {
        name: "privacy-rights"
      }), "Privacy Rights"]
    }), _jsxs(_components.p, {
      children: ["As provided for in the Privacy Rights section of the ", _jsx(MDXLink, {
        text: "Calm Privacy Policy",
        href: "/privacy-policy",
        label: "Calm Privacy Policy"
      }), ", you may make requests about your personal information. Applicable privacy laws provide you certain specific rights with respect to your Consumer Health Data law subject to exceptions. Specifically:"]
    }), _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "The right to know what categories of Consumer Health Data we collect, share, or sell. You also have the right to obtain a list of all third parties and affiliates with whom we have shared or sold your Consumer Health Data, and an active email address or other mechanism that you may use to contact these third parties. If you are a Washington resident or otherwise subject to Washington law, you also have the right to access your Consumer Health Data that we collect, share, or sell."
      }), "\n", _jsx(_components.li, {
        children: "The right to have us, as well as our affiliates, service providers, and contractors, delete the Consumer Health Data we have about you."
      }), "\n", _jsx(_components.li, {
        children: "The right to withdraw consent related to your Consumer Health Data where we rely upon consent to collect, use, or share your Consumer Health Data."
      }), "\n", _jsx(_components.li, {
        children: "The right not to receive discriminatory treatment for the exercise of the privacy rights described above."
      }), "\n"]
    }), _jsxs(_components.p, {
      children: ["You may exercise your rights by contacting ", _jsx(MDXLink, {
        text: "support@calm.com",
        href: "mailto:support@calm.com",
        label: "support@calm.com"
      }), " with your request. As further stated in the Calm Privacy Policy, your request may be denied where an exception applies, including but not limited to, where we are legally required to maintain your Consumer Health Data or if we are unable to authenticate the request using commercially reasonable efforts."]
    }), _jsx(_components.p, {
      children: "We will respond to your request to exercise your right(s) within forty-five (45) days after receipt, and we reserve the right to extend the response time by an additional forty-five (45) days when reasonably necessary and provided consumer notification of the extension is made within the first forty-five (45) days. An authorized agent may submit a request to exercise the opt-out right on your behalf."
    }), _jsx(_components.p, {
      children: "How to appeal decisions about your rights."
    }), _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Nevada:"
        }), " If you are a Nevada resident and your request is denied, you may appeal our decision and we will provide the steps to appeal as part of responding to your request. Within forty-five (45) days of receipt of an appeal, we will inform you in writing of any action taken or not taken, including an explanation of our reasons in reaching the decision. If your appeal is not successful, you may submit a complaint with the Nevada Attorney General at ", _jsx(MDXLink, {
          text: "https://ag.nv.gov/Complaints/CSU_Complaints___FAQ/",
          href: "https://ag.nv.gov/Complaints/CSU_Complaints___FAQ/",
          label: "https://ag.nv.gov/Complaints/CSU_Complaints___FAQ/"
        }), "."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Washington:"
        }), " If you are a Washington resident or located in Washington and your request is denied, you may appeal our decision and we will provide the steps to appeal as part of responding to your request. Within forty-five (45) days of receipt of an appeal, we will inform you in writing of any action taken or not taken, including an explanation of our reasons in reaching the decision. If your appeal is not successful, you may submit a complaint with the Washington State Attorney General at ", _jsx(MDXLink, {
          text: "https://www.atg.wa.gov/file-complaint",
          href: "https://www.atg.wa.gov/file-complaint",
          label: "https://www.atg.wa.gov/file-complaint"
        }), "."]
      }), "\n"]
    })]
  });
}
function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = Object.assign({}, _provideComponents(), props.components);
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, props)
  })) : _createMdxContent(props);
}
export default MDXContent;
